import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { motion } from 'framer-motion';
import { Eye, Target, ShieldCheck, User, Users, HeartHandshake, Award, Handshake } from 'lucide-react';
import PageHero from '@/components/shared/PageHero';
import { useSite } from '@/context/SiteContext';
import { publicApi } from '@/lib/api';
import { getIcon } from '@/lib/icons';
import useSEO from '@/hooks/useSEO';
import SectionHeader from '@/components/shared/SectionHeader';
import RichContent from '@/components/shared/RichContent';
import { stripHtml } from '@/lib/richContent';

const SectionShell = ({ eyebrow, title, description, children, tint = false }) => (
    <section className={`section-pad ${tint ? 'bg-[hsl(var(--secondary))]/50' : ''} relative overflow-hidden`}>
        {tint && <div className="absolute inset-0 grain-bg opacity-60 pointer-events-none" />}
        <div className="container-page relative">
            <SectionHeader eyebrow={eyebrow} title={title} description={description} />
            <div className="mt-8">{children}</div>
        </div>
    </section>
);

export default function AboutPage() {
    const { settings } = useSite();
    const [advisors, setAdvisors] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [associations, setAssociations] = useState([]);

    useSEO({
        title: 'About Us',
        description:
            'Learn about VVVSI Trust — vision, mission, chairman Actor Vijay Sethupathi, managing trustee I.B. Veerarahavan, Advisory Committee, Volunteers program, Our Sectors, and organisations we are Connected With.',
    });

    useEffect(() => {
        publicApi.getAdvisors().then(setAdvisors).catch(() => {});
        publicApi.getSectors().then(setSectors).catch(() => {});
        publicApi.getAssociations().then(setAssociations).catch(() => {});
    }, []);

    if (!settings) return null;

    // Group associations by category for cleaner display
    const associationsByCategory = associations.reduce((acc, a) => {
        const key = a.category || 'Other';
        acc[key] = acc[key] || [];
        acc[key].push(a);
        return acc;
    }, {});

    return (
        <div data-testid="about-page">
            <PageHero
                eyebrow="About Us"
                title={stripHtml(settings.trust_name) || settings.trust_name}
                subtitle={stripHtml(settings.tagline)}
            />

            {/* Overview + Vision/Mission */}
            <section className="section-pad">
                <div className="container-page grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8">
                        <RichContent
                            html={settings.about_short}
                            className="text-lg leading-relaxed text-foreground/85"
                        />
                        <RichContent
                            html={settings.about_long}
                            className="mt-4 leading-relaxed text-foreground/85"
                        />
                        <div className="mt-8 grid sm:grid-cols-2 gap-4">
                            <Card className="bg-card border-border">
                                <CardContent className="p-6">
                                    <Eye className="h-6 w-6 text-[hsl(var(--primary))] mb-3" />
                                    <div className="font-display font-semibold text-xl mb-1">
                                        Our Vision
                                    </div>
                                    <RichContent
                                        html={settings.vision}
                                        className="text-sm text-muted-foreground leading-relaxed"
                                    />
                                </CardContent>
                            </Card>
                            <Card className="bg-card border-border">
                                <CardContent className="p-6">
                                    <Target className="h-6 w-6 text-[hsl(var(--accent))] mb-3" />
                                    <div className="font-display font-semibold text-xl mb-1">
                                        Our Mission
                                    </div>
                                    <RichContent
                                        html={settings.mission}
                                        className="text-sm text-muted-foreground leading-relaxed"
                                    />
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                    <aside className="lg:col-span-4 space-y-5">
                        <Card className="bg-card border-border">
                            <CardContent className="p-6">
                                <div className="flex items-center gap-2 text-[hsl(var(--accent))] mb-3">
                                    <ShieldCheck className="h-4 w-4" />
                                    <span className="text-xs font-semibold uppercase tracking-widest">
                                        Compliance
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {(settings.compliance_badges || []).map((b) => (
                                        <Badge
                                            key={b}
                                            variant="secondary"
                                            className="bg-[hsl(var(--secondary))] text-[hsl(var(--primary))] font-semibold"
                                        >
                                            {b} Certified
                                        </Badge>
                                    ))}
                                </div>
                                <Separator className="my-4" />
                                <div className="space-y-3 text-sm">
                                    <div className="flex items-center gap-3">
                                        <div className="h-12 w-12 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                                            {settings.chairman_photo ? (
                                                <img
                                                    src={settings.chairman_photo}
                                                    alt=""
                                                    className="h-full w-full object-cover"
                                                />
                                            ) : (
                                                <User className="h-5 w-5 text-muted-foreground" />
                                            )}
                                        </div>
                                        <div>
                                            <div className="font-semibold">
                                                {settings.chairman_name}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {settings.chairman_title}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="h-12 w-12 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                                            {settings.managing_trustee_photo ? (
                                                <img
                                                    src={settings.managing_trustee_photo}
                                                    alt=""
                                                    className="h-full w-full object-cover"
                                                />
                                            ) : (
                                                <User className="h-5 w-5 text-muted-foreground" />
                                            )}
                                        </div>
                                        <div>
                                            <div className="font-semibold">
                                                {settings.managing_trustee_name}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {settings.managing_trustee_title}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </aside>
                </div>
            </section>

            {/* Advisory Committee */}
            <SectionShell
                eyebrow="Advisory Committee"
                title="Guided by leaders across sectors."
                description="A cross-disciplinary group of advisors who help VVVSI stay accountable, ambitious and effective."
                tint
            >
                <div
                    className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
                    data-testid="about-advisors"
                >
                    {advisors.map((a, i) => (
                        <motion.div
                            key={a.id}
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                            viewport={{ once: true, margin: '-40px' }}
                        >
                            <Card className="h-full bg-card border-border card-lift">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="h-14 w-14 rounded-full overflow-hidden bg-muted flex items-center justify-center shrink-0">
                                            {a.photo_url ? (
                                                <img
                                                    src={a.photo_url}
                                                    alt={a.name}
                                                    className="h-full w-full object-cover"
                                                />
                                            ) : (
                                                <User className="h-6 w-6 text-muted-foreground" />
                                            )}
                                        </div>
                                        <div>
                                            <div className="font-semibold">{a.name}</div>
                                            <div className="text-xs text-[hsl(var(--primary))] font-medium">
                                                {a.role}
                                            </div>
                                        </div>
                                    </div>
                                    <RichContent
                                        html={a.bio}
                                        className="text-sm text-muted-foreground leading-relaxed"
                                    />
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            </SectionShell>

            {/* Volunteers */}
            <SectionShell
                eyebrow="Our Volunteers"
                title="A 3,400+ strong grassroots family."
                description="The volunteer network is the beating heart of VVVSI — students, professionals, retired officers, doctors and teachers giving time to programs across Tamil Nadu & Puducherry."
            >
                <div className="grid lg:grid-cols-12 gap-8 items-start" data-testid="about-volunteers">
                    <div className="lg:col-span-7 text-foreground/85 leading-relaxed">
                        <RichContent html={settings.volunteer_program_description} />
                        <ul className="mt-4 space-y-2 text-sm">
                            <li className="flex items-start gap-2">
                                <HeartHandshake className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))]" />
                                Structured induction, mentor pairing and safeguarding training.
                            </li>
                            <li className="flex items-start gap-2">
                                <Users className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))]" />
                                District-wise volunteer leads for coordinated program execution.
                            </li>
                            <li className="flex items-start gap-2">
                                <Award className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))]" />
                                Formal certification of volunteer hours and annual recognition meets.
                            </li>
                        </ul>
                    </div>
                    <div className="lg:col-span-5 grid grid-cols-2 gap-3">
                        {[
                            ['3,400+', 'Active volunteers'],
                            ['30+', 'Districts covered'],
                            ['850+', 'Deployments per year'],
                            ['48h', 'Disaster response SLA'],
                        ].map(([v, l]) => (
                            <Card key={l} className="bg-card border-border">
                                <CardContent className="p-5 text-center">
                                    <div className="font-display font-semibold text-3xl text-[hsl(var(--primary))]">
                                        {v}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">{l}</div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </SectionShell>

            {/* Our Sectors */}
            <SectionShell
                eyebrow="Our Sectors"
                title="Six sectors, one integrated mission."
                description="VVVSI runs programs across an interconnected set of sectors — designed to reinforce each other."
            >
                <div
                    className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
                    data-testid="about-sectors"
                >
                    {sectors.map((s, i) => {
                        const Icon = getIcon(s.icon);
                        return (
                            <motion.div
                                key={s.id}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: i * 0.05 }}
                                viewport={{ once: true, margin: '-40px' }}
                            >
                                <Card className="h-full bg-card border-border card-lift overflow-hidden">
                                    <div className="aspect-[16/9] bg-muted overflow-hidden">
                                        <img
                                            src={s.image_url}
                                            alt={s.name}
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <CardContent className="p-5">
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="h-9 w-9 rounded-lg bg-[hsl(var(--primary))]/8 text-[hsl(var(--primary))] flex items-center justify-center">
                                                <Icon className="h-4 w-4" />
                                            </div>
                                            <div className="font-semibold">{s.name}</div>
                                        </div>
                                        <p className="text-sm text-muted-foreground leading-relaxed">
                                            {stripHtml(s.description)}
                                        </p>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        );
                    })}
                </div>
            </SectionShell>

            {/* We Are Connected With */}
            <SectionShell
                eyebrow="We Are Connected With"
                title="Partners, associations and networks."
                description="VVVSI is embedded in a broader ecosystem of governance bodies, industry networks and community associations."
                tint
            >
                <div className="space-y-8" data-testid="about-connections">
                    {Object.entries(associationsByCategory).map(([category, list]) => (
                        <div key={category}>
                            <div className="flex items-center gap-2 mb-3">
                                <Handshake className="h-4 w-4 text-[hsl(var(--accent))]" />
                                <div className="text-sm font-semibold text-[hsl(var(--primary))]">
                                    {category}
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {list.map((a) => (
                                    <a
                                        key={a.id}
                                        href={a.website && a.website !== '#' ? a.website : undefined}
                                        target={a.website && a.website !== '#' ? '_blank' : undefined}
                                        rel="noreferrer noopener"
                                        className="block"
                                    >
                                        <Card className="h-full bg-card border-border card-lift">
                                            <CardContent className="p-4">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <div className="h-10 w-16 flex items-center justify-center rounded bg-white p-1">
                                                        {a.logo_url ? (
                                                            <img
                                                                src={a.logo_url}
                                                                alt={a.name}
                                                                className="max-h-8 max-w-full object-contain"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                }}
                                                            />
                                                        ) : null}
                                                    </div>
                                                    <div className="font-semibold text-sm">
                                                        {a.name}
                                                    </div>
                                                </div>
                                                <div className="text-xs text-muted-foreground leading-relaxed">
                                                    {stripHtml(a.description)}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </a>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </SectionShell>
        </div>
    );
}
